"use client";

import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useCallback, useMemo, useTransition } from "react";
import type { ActiveChip, FilterGroup, FiltersV2State } from "./types";

/**
 * URL-driven filter state. Reads searchParams, computes chips, and exposes
 * mutators that push back into the URL with `router.replace` (no scroll reset).
 *
 * Why URL-driven: filters survive refresh, are shareable, and SSR can render
 * pre-filtered results without hydration mismatch.
 */
export function useFiltersState(groups: FilterGroup[]): {
  state: FiltersV2State;
  set: (param: string, value: string | string[] | [number, number] | null) => void;
  toggleMulti: (param: string, value: string) => void;
  removeChip: (chip: ActiveChip) => void;
  clearAll: () => void;
  isPending: boolean;
} {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();

  const values = useMemo<FiltersV2State["values"]>(() => {
    const out: FiltersV2State["values"] = {};
    for (const g of groups) {
      const raw = searchParams.getAll(g.param);
      if (g.kind === "range") {
        const min = searchParams.get(`${g.param}Min`);
        const max = searchParams.get(`${g.param}Max`);
        if (min || max) {
          out[g.param] = [
            min ? Number(min) : (g.min ?? 0),
            max ? Number(max) : (g.max ?? 0),
          ];
        } else {
          out[g.param] = null;
        }
      } else if (g.kind === "multi") {
        out[g.param] = raw.length > 0 ? raw : null;
      } else {
        out[g.param] = raw[0] ?? null;
      }
    }
    return out;
  }, [groups, searchParams]);

  const chips = useMemo<ActiveChip[]>(() => {
    const out: ActiveChip[] = [];
    for (const g of groups) {
      const v = values[g.param];
      if (v == null) continue;
      if (g.kind === "range" && Array.isArray(v) && typeof v[0] === "number") {
        const [min, max] = v as [number, number];
        if (g.min !== undefined && min > g.min) {
          out.push({ groupId: g.id, param: g.param, value: "min", label: `${g.label} ≥ ${min}` });
        }
        if (g.max !== undefined && max < g.max) {
          out.push({ groupId: g.id, param: g.param, value: "max", label: `${g.label} ≤ ${max}` });
        }
      } else if (g.kind === "multi" && Array.isArray(v)) {
        for (const val of v as string[]) {
          const opt = g.options?.find((o) => o.value === val);
          out.push({ groupId: g.id, param: g.param, value: val, label: opt?.label ?? val });
        }
      } else if (typeof v === "string") {
        const opt = g.options?.find((o) => o.value === v);
        out.push({ groupId: g.id, param: g.param, value: v, label: opt?.label ?? v });
      }
    }
    return out;
  }, [groups, values]);

  const push = useCallback(
    (next: URLSearchParams) => {
      // `replace` keeps history clean; transitions keep the UI responsive.
      startTransition(() => {
        router.replace(`${pathname}?${next.toString()}`, { scroll: false });
      });
    },
    [pathname, router]
  );

  const set = useCallback(
    (param: string, value: string | string[] | [number, number] | null) => {
      const params = new URLSearchParams(searchParams.toString());
      params.delete(param);
      params.delete(`${param}Min`);
      params.delete(`${param}Max`);
      if (value == null) {
        // already deleted
      } else if (Array.isArray(value) && typeof value[0] === "number") {
        const [min, max] = value as [number, number];
        params.set(`${param}Min`, String(min));
        params.set(`${param}Max`, String(max));
      } else if (Array.isArray(value)) {
        for (const v of value as string[]) params.append(param, v);
      } else {
        params.set(param, value);
      }
      // Reset pagination when filters change.
      params.delete("page");
      push(params);
    },
    [push, searchParams]
  );

  const toggleMulti = useCallback(
    (param: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      const current = params.getAll(param);
      params.delete(param);
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      for (const v of next) params.append(param, v);
      params.delete("page");
      push(params);
    },
    [push, searchParams]
  );

  const removeChip = useCallback(
    (chip: ActiveChip) => {
      const group = groups.find((g) => g.id === chip.groupId);
      if (!group) return;
      const params = new URLSearchParams(searchParams.toString());
      if (group.kind === "range") {
        if (chip.value === "min") params.delete(`${group.param}Min`);
        if (chip.value === "max") params.delete(`${group.param}Max`);
      } else if (group.kind === "multi") {
        const current = params.getAll(group.param).filter((v) => v !== chip.value);
        params.delete(group.param);
        for (const v of current) params.append(group.param, v);
      } else {
        params.delete(group.param);
      }
      params.delete("page");
      push(params);
    },
    [groups, push, searchParams]
  );

  const clearAll = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());
    for (const g of groups) {
      params.delete(g.param);
      params.delete(`${g.param}Min`);
      params.delete(`${g.param}Max`);
    }
    params.delete("page");
    push(params);
  }, [groups, push, searchParams]);

  return {
    state: { values, chips, count: chips.length },
    set,
    toggleMulti,
    removeChip,
    clearAll,
    isPending,
  };
}
